/**
 * Documents tab of the business page (Business Account Pages slice 2, migration 2026-10-06-314).
 * Upload, view (opens in a new tab via a 60-second signed link), download, re-categorise and delete files.
 * Internal staff only; files live in the private `organization-files` bucket under this organization's folder.
 */

import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CloudArrowUpIcon, DocumentIcon } from "@heroicons/react/24/outline";
import Card from "../ui/Card";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import { useConfirmDialog } from "../ui/ConfirmDialog";
import { formatDateMDY } from "../../utils/formatters";
import {
  DOCUMENT_CATEGORIES,
  FILE_ACCEPT,
  checkUploadable,
  deleteOrganizationDocument,
  documentCategoryLabel,
  formatFileSize,
  getDocumentSignedUrl,
  getOrganizationDocuments,
  updateOrganizationDocument,
  uploadOrganizationDocument,
  verifyOrganizationDocument,
} from "../../services/documents.service";
import type {
  DocumentCategory,
  OrganizationDocument,
} from "../../services/documents.service";

interface Props {
  organizationId: string;
  canEdit: boolean;
  defaultCategory?: DocumentCategory;
}

const OrganizationDocumentsTab: React.FC<Props> = ({
  organizationId,
  canEdit,
  defaultCategory = "other",
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [category, setCategory] = useState<DocumentCategory>(defaultCategory);
  const [filter, setFilter] = useState<"all" | DocumentCategory>("all");
  const [uploading, setUploading] = useState(false);

  const {
    data: docs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization-documents", organizationId],
    queryFn: () => getOrganizationDocuments(organizationId),
  });
  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ["organization-documents", organizationId],
    });
    queryClient.invalidateQueries({
      queryKey: ["organization-documents-count", organizationId],
    });
  };

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      const problem = checkUploadable(f);
      if (problem) {
        showToast("error", problem);
        continue;
      }
      try {
        await uploadOrganizationDocument(organizationId, f, category);
        ok += 1;
      } catch (e) {
        showToast(
          "error",
          `“${f.name}” was not uploaded: ${(e as Error).message}`,
        );
      }
    }
    setUploading(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (ok > 0) {
      showToast("success", ok === 1 ? "File uploaded" : `${ok} files uploaded`);
    }
    invalidate();
  };

  const openFile = async (d: OrganizationDocument, download: boolean) => {
    // open the tab synchronously (popup blockers), then point it at the signed link
    const win = download ? null : window.open("about:blank", "_blank");
    try {
      const url = await getDocumentSignedUrl(d, download);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.rel = "noopener";
        a.click();
      } else if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
      }
    } catch (e) {
      win?.close();
      showToast(
        "error",
        `The file could not be opened: ${(e as Error).message}`,
      );
    }
  };

  const recategorise = useMutation({
    mutationFn: ({ id, c }: { id: string; c: DocumentCategory }) =>
      updateOrganizationDocument(id, { category: c }),
    onSuccess: invalidate,
    onError: (e: Error) => showToast("error", e.message),
  });
  const recheck = useMutation({
    mutationFn: (id: string) => verifyOrganizationDocument(id),
    onSuccess: () => {
      invalidate();
      showToast("success", "File checked");
    },
    onError: (e: Error) => {
      invalidate();
      showToast("error", e.message);
    },
  });
  const remove = useMutation({
    mutationFn: (d: OrganizationDocument) => deleteOrganizationDocument(d),
    onSuccess: () => {
      invalidate();
      showToast("success", "File deleted");
    },
    onError: (e: Error) => {
      invalidate();
      showToast("error", `The file could not be deleted: ${e.message}`);
    },
  });

  const shown =
    filter === "all" ? docs : docs.filter((d) => d.category === filter);
  const sel = "px-3 py-2 border border-gray-300 rounded-lg text-sm";

  return (
    <Card>
      <div
        className="flex flex-wrap items-center justify-between gap-3 mb-6"
        data-testid="documents-tab"
      >
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
          <p className="text-sm text-gray-500">
            Agreements, vendor paperwork and other files for this organization.
            Visible to internal staff only.
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-gray-700" htmlFor="upload-category">
              Category
            </label>
            <select
              id="upload-category"
              className={sel}
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory)}
            >
              {DOCUMENT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={FILE_ACCEPT}
              className="hidden"
              data-testid="document-file-input"
              onChange={(e) => onFiles(e.target.files)}
            />
            <Button
              onClick={() => inputRef.current?.click()}
              isLoading={uploading}
            >
              <CloudArrowUpIcon className="h-5 w-5 mr-1" />
              Upload files
            </Button>
          </div>
        )}
      </div>

      <div className="mb-3 flex items-center gap-2 text-sm">
        <label htmlFor="doc-filter" className="text-gray-700">
          Show
        </label>
        <select
          id="doc-filter"
          className={sel}
          value={filter}
          onChange={(e) =>
            setFilter(e.target.value as "all" | DocumentCategory)
          }
        >
          <option value="all">All categories</option>
          {DOCUMENT_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <span className="text-gray-500">
          PDF, images, Word, Excel, PowerPoint, CSV or text; 25 MB each.
        </span>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-700">Files could not be loaded.</p>
      ) : shown.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No files yet
          </h3>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="min-w-full divide-y divide-gray-200"
            data-testid="documents-table"
          >
            <thead className="bg-gray-50">
              <tr>
                {["Name", "Category", "Size", "Uploaded", ""].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shown.map((d) => (
                <tr key={d.id} data-testid="document-row">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 break-all">
                    {d.name}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {canEdit ? (
                      <select
                        aria-label={`Category of ${d.name}`}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        value={d.category}
                        onChange={(e) =>
                          recategorise.mutate({
                            id: d.id,
                            c: e.target.value as DocumentCategory,
                          })
                        }
                      >
                        {DOCUMENT_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      documentCategoryLabel(d.category)
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {formatFileSize(d.file_size_bytes)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDateMDY(d.uploaded_at)}
                    {d.uploader && (
                      <span className="block text-gray-500">
                        {d.uploader.full_name || d.uploader.email}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm whitespace-nowrap space-x-3">
                    {d.verified_at ? (
                      <>
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => openFile(d, false)}
                        >
                          View
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => openFile(d, true)}
                        >
                          Download
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          className="text-amber-700"
                          data-testid="document-unverified"
                          title="The file's content has not been checked yet, so it cannot be opened."
                        >
                          Not checked yet
                        </span>
                        {canEdit && (
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => recheck.mutate(d.id)}
                            disabled={recheck.isPending}
                          >
                            Check again
                          </button>
                        )}
                      </>
                    )}
                    {canEdit && (
                      <button
                        className="text-red-600 hover:text-red-800"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: "Delete file?",
                              message: `“${d.name}” will be permanently deleted for everyone.`,
                              confirmText: "Delete",
                              variant: "danger",
                            })
                          ) {
                            remove.mutate(d);
                          }
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialogComponent />
    </Card>
  );
};

export default OrganizationDocumentsTab;
